/**
 * Diccionario de métricas técnicas del triatlón (M2.32.E).
 *
 * Sirve como única fuente de verdad para el componente `MetricTooltip`. Cada
 * entrada incluye:
 *   - `fullName`: nombre humano largo en español (con acrónimo en paréntesis si aplica).
 *   - `short`: acrónimo / etiqueta corta tal como aparece típicamente en la UI.
 *   - `definition`: 1-2 frases explicando qué mide la métrica.
 *   - `formula`: cadena opcional con la fórmula breve (renderizada como `<code>`).
 *   - `interpretation`: cómo leerla — incluye umbrales y semáforos cuando aplican.
 *
 * Todo el contenido en español neutro; las fórmulas usan notación matemática
 * estándar (sin LaTeX) para que el tooltip funcione sin librerías extra.
 */
export type MetricKey =
  | 'ctl'
  | 'atl'
  | 'tsb'
  | 'rampCtl'
  | 'monotony'
  | 'strain'
  | 'acwr'
  | 'compliance'
  | 'tss'
  | 'np'
  | 'if'
  | 'ftp'
  | 'lthr'
  | 'tPace'
  | 'tPaceRun'
  | 'tPaceSwim'
  | 'bmr'
  | 'polarization'
  | 'readiness'
  | 'hrv'
  | 'rhr'
  | 'sleep'
  | 'sCtl'
  | 'bCtl'
  | 'rCtl'
  | 'disciplineBalance'
  | 'daysSinceOff'
  | 'daysSinceDiscipline'
  | 'nextARace'
  | 'intensityZone'
  | 'avgHr'
  | 'maxHr'
  | 'avgPower'
  | 'maxPower'
  | 'sufferScore'
  | 'kilojoules'
  | 'calories'
  | 'elevationGain'
  | 'targetWeeklyHours'
  | 'intensityDistribution'
  | 'formStatus';

export interface MetricEntry {
  /** Nombre completo en español, p.ej. "Chronic Training Load". */
  fullName: string;
  /** Acrónimo o etiqueta corta usada en la UI, p.ej. "CTL". */
  short: string;
  /** Definición concisa (1-2 frases). */
  definition: string;
  /** Fórmula breve opcional (texto plano). */
  formula?: string;
  /** Interpretación práctica con umbrales y semáforos. */
  interpretation: string;
}

export const METRICS_GLOSSARY: Record<MetricKey, MetricEntry> = {
  ctl: {
    fullName: 'Chronic Training Load (Fitness)',
    short: 'CTL',
    definition:
      'Tu nivel de fitness a largo plazo. Media exponencial (EMA) de 42 días del TSS diario.',
    formula: 'CTL_hoy = TSS_hoy × λ + CTL_ayer × (1 − λ), con λ = 1 − exp(−1/42)',
    interpretation:
      'Subir CTL = ganar fitness. Bajar = perder. Crecimiento (ramp) > 8/sem aumenta el riesgo de lesión.',
  },
  atl: {
    fullName: 'Acute Training Load (Fatiga)',
    short: 'ATL',
    definition:
      'Fatiga reciente. Media exponencial (EMA) de 7 días del TSS diario.',
    formula: 'ATL_hoy = TSS_hoy × λ + ATL_ayer × (1 − λ), con λ = 1 − exp(−1/7)',
    interpretation:
      'ATL alto y subiendo = días duros recientes. Útil para planificar recuperación tras bloques.',
  },
  tsb: {
    fullName: 'Training Stress Balance (Forma)',
    short: 'TSB',
    definition:
      'Indicador de “forma”: cuán fresco estás. Diferencia entre fitness crónico y fatiga aguda.',
    formula: 'TSB = CTL − ATL',
    interpretation:
      '> +15: muy fresco (peak/taper). 0 a +15: forma. −10 a 0: cargando. < −20: sobrecarga.',
  },
  rampCtl: {
    fullName: 'Ramp Rate del CTL',
    short: 'Ramp',
    definition:
      'Cambio del CTL en los últimos 7 días. Indica con qué rapidez ganas (o pierdes) fitness.',
    formula: 'Ramp = CTL_hoy − CTL_hace_7d',
    interpretation:
      '0–5/sem ok. > +5 acelerando, > +8 riesgo de lesión, > +12 peligro. Negativo prolongado = desentreno.',
  },
  monotony: {
    fullName: 'Monotonía de Foster',
    short: 'Mon',
    definition:
      'Variación del estímulo en la semana: media del TSS dividida por su desviación estándar (7d).',
    formula: 'Monotonía = media(TSS_7d) / desv_estándar(TSS_7d)',
    interpretation:
      '< 1.5 ok. 1.5–2.0 cuidado. > 2.0 sin descanso, riesgo elevado de enfermedad/lesión.',
  },
  strain: {
    fullName: 'Strain semanal',
    short: 'Strain',
    definition:
      'Carga psicofísica acumulada en la semana, ponderada por monotonía.',
    formula: 'Strain = TSS_7d × monotonía',
    interpretation:
      '< 4000 ok. 4000–6000 elevado. > 6000 sobrecarga; inserta día(s) off.',
  },
  acwr: {
    fullName: 'Acute:Chronic Workload Ratio',
    short: 'ACWR',
    definition:
      'Cociente entre carga aguda (7d) y crónica media (28d). Predictor clásico de lesiones (Gabbett).',
    formula: 'ACWR = TSS_7d / media(TSS_28d)',
    interpretation:
      'Sweet spot 0.8–1.3. 1.3–1.5 vigila. > 1.5 o < 0.8 = riesgo elevado.',
  },
  compliance: {
    fullName: 'Cumplimiento del plan (28d)',
    short: 'Plan',
    definition:
      'Cuánto del TSS planificado has ejecutado en los últimos 28 días.',
    formula: 'Compliance = TSS_ejecutado_28d / TSS_planificado_28d',
    interpretation:
      '80–110% ok. 70–80% o 110–120% warn. < 70% o > 120% revisa expectativas vs realidad.',
  },
  tss: {
    fullName: 'Training Stress Score',
    short: 'TSS',
    definition:
      'Estrés total de un entreno, normalizado: 100 TSS = 1 h al umbral (FTP test). Permite comparar sesiones de distinta intensidad y duración.',
    formula:
      'Power: (h × NP × IF) / FTP × 3600 / 100. HR: similar con LTHR. Sin sensor: estimado por zona × duración.',
    interpretation:
      '< 150 sesión moderada. 150–300 dura. > 300 sesión larga/muy dura. Suma diaria → CTL/ATL.',
  },
  np: {
    fullName: 'Normalized Power',
    short: 'NP',
    definition:
      'Potencia ajustada por variabilidad (cuarta raíz de la media de potencia^4 en ventanas de 30 s). Refleja mejor el coste fisiológico que la potencia media.',
    formula: 'NP = ⁴√( media( P_30s ⁴ ) )',
    interpretation:
      'Si NP es mucho mayor que avg power → entreno con muchos picos (intervalos/MTB), más exigente.',
  },
  if: {
    fullName: 'Intensity Factor',
    short: 'IF',
    definition:
      'Intensidad relativa al umbral. Ratio entre NP y FTP.',
    formula: 'IF = NP / FTP',
    interpretation:
      '< 0.75 recovery/Z2. 0.75–0.85 endurance/tempo. 0.85–0.95 sweet spot/umbral. > 1.05 sostenido es muy duro (típico de tests cortos).',
  },
  ftp: {
    fullName: 'Functional Threshold Power',
    short: 'FTP',
    definition:
      'Watts máximos que puedes sostener durante ~1 hora. Pivote de las zonas Coggan (Z1–Z7) y del cálculo de TSS por potencia.',
    formula: 'FTP ≈ 95 % del mejor 20 min',
    interpretation:
      'Mejora con entreno; conviene re-testar cada 6–8 semanas o cuando notes que cuesta menos. Cambiarla recalcula tu CTL/ATL histórico.',
  },
  lthr: {
    fullName: 'Lactate Threshold Heart Rate',
    short: 'LTHR',
    definition:
      'Frecuencia cardiaca al umbral de lactato. Pivote de las zonas Friel (Z1–Z5) cuando no tienes potenciómetro.',
    formula: 'LTHR ≈ FC media de los últimos 20 min de un test de 30 min máx',
    interpretation:
      'No depende mucho del fitness sino de la genética; se ajusta poco. Cambia con la edad y muy gradualmente con el entrenamiento.',
  },
  tPace: {
    fullName: 'Threshold pace',
    short: 'T-pace',
    definition:
      'Ritmo sostenible al umbral. Pivote de las zonas en running (min/km) y natación (min/100 m) cuando no hay HR/potencia.',
    interpretation:
      'Carrera: ritmo mantenible ~1 h. Natación: ritmo de un T-30 (30 min máx) ajustado.',
  },
  tPaceRun: {
    fullName: 'Threshold pace (running)',
    short: 'T-pace run',
    definition:
      'Ritmo de carrera sostenible ~1 h al umbral. Base de las zonas Friel/Daniels en running.',
    interpretation:
      'Más bajo = más rápido (mm:ss/km). Mejora con entreno; revisa tras carreras o tests específicos.',
  },
  tPaceSwim: {
    fullName: 'Threshold pace (swim)',
    short: 'T-pace swim',
    definition:
      'Ritmo de natación sostenible al umbral (mm:ss / 100 m).',
    interpretation:
      'Suele estimarse con un T-30 o un CSS. Más bajo = más rápido. Define zonas y series.',
  },
  bmr: {
    fullName: 'Basal Metabolic Rate',
    short: 'BMR',
    definition:
      'Calorías que tu cuerpo quema en reposo absoluto en 24 h. Calculado con Mifflin-St Jeor.',
    formula:
      'Hombres: 10·peso + 6.25·talla − 5·edad + 5. Mujeres: 10·peso + 6.25·talla − 5·edad − 161 (kcal/día)',
    interpretation:
      'Base mínima de kcal. Para fijar objetivos diarios multiplica por factor de actividad (1.4–2.0 según volumen).',
  },
  polarization: {
    fullName: 'Polarización 80/20',
    short: 'Pol',
    definition:
      'Porcentaje del tiempo total entrenado en Z1–Z2 (aeróbico) frente al total. Modelo polarizado: la mayor parte fácil, una pequeña parte muy dura.',
    formula: 'Pol % = minutos(Z1 + Z2) / minutos_totales × 100',
    interpretation:
      '≥ 75 % en base, ≥ 65 % en build = ok. < 65 % indica demasiado tempo/umbral (zona gris).',
  },
  readiness: {
    fullName: 'Readiness biométrico',
    short: 'Readiness',
    definition:
      'Score 0–100 derivado de las desviaciones de HRV, RHR y sueño frente a tus baselines rolling de 60 d.',
    interpretation:
      '≥ 70 verde (entrena duro). 50–70 amarillo (modera). < 50 rojo (recovery o off).',
  },
  hrv: {
    fullName: 'Heart Rate Variability',
    short: 'HRV',
    definition:
      'Variabilidad de la frecuencia cardiaca matutina (RMSSD en ms). Refleja el equilibrio del sistema nervioso autónomo.',
    interpretation:
      'Subir vs tu baseline = mejor recuperación. Bajar = fatiga, estrés o enfermedad incipiente. Más útil la tendencia que un día aislado.',
  },
  rhr: {
    fullName: 'Resting Heart Rate',
    short: 'RHR',
    definition:
      'Frecuencia cardiaca en reposo, medida idealmente al despertar y antes de levantarte.',
    interpretation:
      'Bajar vs baseline = mejor condición. Subir 5–7 lpm = estrés, falta de sueño, enfermedad o sobreentrenamiento.',
  },
  sleep: {
    fullName: 'Horas de sueño',
    short: 'Sueño',
    definition:
      'Tiempo total dormido la noche anterior, en horas. Componente clave de la readiness.',
    interpretation:
      '≥ 7.5 h ideal para deportistas de resistencia. < 6 h sostenido degrada CTL alcanzable y dispara RHR.',
  },
  sCtl: {
    fullName: 'CTL de natación',
    short: 'sCTL',
    definition:
      'CTL desagregado de la disciplina natación (swim). EMA 42 d del TSS específico de nataciones.',
    interpretation:
      'Útil para detectar si tu CTL global esconde un desbalance (p.ej. mucho bike, swim a 0).',
  },
  bCtl: {
    fullName: 'CTL de ciclismo',
    short: 'bCTL',
    definition:
      'CTL desagregado de la disciplina ciclismo (bike). EMA 42 d del TSS específico de bicis.',
    interpretation:
      'Suele ser el más alto en triatleta de larga distancia. Estabilízalo en base, polarízalo en build.',
  },
  rCtl: {
    fullName: 'CTL de carrera',
    short: 'rCTL',
    definition:
      'CTL desagregado de la disciplina carrera (run). EMA 42 d del TSS específico de runs.',
    interpretation:
      'El que más correlaciona con lesiones; vigila ramp > +6/sem en running aunque el CTL global crezca poco.',
  },
  disciplineBalance: {
    fullName: 'Balance de disciplinas',
    short: 'Balance',
    definition:
      'Verifica que ninguna disciplina core (swim/bike/run) lleva más de 7 días sin actividad.',
    interpretation:
      '“OK” = todas tocadas en la última semana. Aviso = una disciplina lleva > 7 d sin entreno (warn) o > 14 d (danger).',
  },
  daysSinceOff: {
    fullName: 'Días sin descanso',
    short: 'Sin off',
    definition:
      'Días consecutivos con TSS > 0 (sin un día completo de descanso).',
    interpretation:
      '< 7 ok. 7–9 vigila, planifica un off. ≥ 10 inserta un día off cuanto antes.',
  },
  daysSinceDiscipline: {
    fullName: 'Días sin la disciplina',
    short: 'Sin disciplina',
    definition:
      'Días transcurridos desde la última sesión de una disciplina concreta (swim, bike o run).',
    interpretation:
      '> 7 d sin tocar una disciplina core = perderás adaptación específica. Reincorpórala con sesión Z1–Z2.',
  },
  nextARace: {
    fullName: 'Próxima carrera A',
    short: 'Próx. carrera A',
    definition:
      'Tu siguiente competición con prioridad A (objetivo principal). Pivote para tapering y picos.',
    interpretation:
      'A < 21 d → fase taper/race week. A < 90 d → ya estás en build/peak. > 90 d → base.',
  },
  intensityZone: {
    fullName: 'Zona de intensidad',
    short: 'Zona',
    definition:
      'Zona de entrenamiento (Z1–Z5/Z7) según FTP (potencia), LTHR (FC) o T-pace (ritmo).',
    interpretation:
      'Z1–Z2 aeróbico/base. Z3 tempo. Z4 umbral. Z5+ VO2max/anaeróbico. El modelo polarizado prioriza Z1–Z2 + algo de Z4+.',
  },
  avgHr: {
    fullName: 'Frecuencia cardiaca media',
    short: 'FC media',
    definition:
      'Media de la frecuencia cardiaca durante la sesión (lpm).',
    interpretation:
      'Comparada con LTHR estima la zona dominante. Cardiac drift > 5 % a misma potencia = fatiga acumulada o calor.',
  },
  maxHr: {
    fullName: 'Frecuencia cardiaca máxima',
    short: 'FC máx',
    definition:
      'Pico de FC alcanzado durante la sesión.',
    interpretation:
      'Útil para detectar esfuerzos puntuales muy altos. La FCmáx real (test) ronda 220 − edad pero varía mucho por persona.',
  },
  avgPower: {
    fullName: 'Potencia media',
    short: 'Potencia media',
    definition:
      'Media aritmética de los watts durante la sesión (solo bike).',
    interpretation:
      'En recorridos llanos ≈ NP; en montaña/intervalos NP es siempre mayor y refleja mejor el coste real.',
  },
  maxPower: {
    fullName: 'Potencia máxima',
    short: 'Potencia máx',
    definition:
      'Pico de watts alcanzado (1 s) durante la sesión.',
    interpretation:
      'Indicador de potencia neuromuscular (sprints, arrancadas). Para fitness aeróbico mira NP/IF, no el pico.',
  },
  sufferScore: {
    fullName: 'Suffer Score (Strava)',
    short: 'Suffer',
    definition:
      'Métrica propietaria de Strava basada en tiempo en cada zona de FC. Equivalente burdo a TSS por HR.',
    interpretation:
      '< 50 fácil. 50–100 sólido. 100–150 duro. > 150 muy duro.',
  },
  kilojoules: {
    fullName: 'Energía (kJ)',
    short: 'kJ',
    definition:
      'Trabajo mecánico total realizado en bici (potencia × tiempo).',
    formula: 'kJ ≈ Potencia_media (W) × duración (h) × 3.6',
    interpretation:
      'Aproximación: 1 kJ ≈ 1 kcal (la eficiencia muscular ~24 % se compensa con factores). Útil para reponer carbos.',
  },
  calories: {
    fullName: 'Calorías',
    short: 'kcal',
    definition:
      'Gasto energético total estimado durante la sesión.',
    interpretation:
      'Reposición: ~60–90 g CHO/h en entrenos > 90 min. Cubre déficit total con la dieta diaria + BMR × factor actividad.',
  },
  elevationGain: {
    fullName: 'Desnivel positivo',
    short: 'Desnivel +',
    definition:
      'Metros totales subidos durante la sesión.',
    interpretation:
      'En run > 600 m incrementa estrés excéntrico y TSS efectivo. En bici dispara NP por los picos en cuestas.',
  },
  targetWeeklyHours: {
    fullName: 'Horas objetivo semanales',
    short: 'h/sem',
    definition:
      'Volumen total semanal planificado para la fase, en horas (suma de todas las disciplinas).',
    interpretation:
      'Triatleta sprint: 6–10 h. Half-IM: 8–14 h. IM: 12–20 h. La fase build sube ~10 % cada 3–4 semanas con descarga.',
  },
  intensityDistribution: {
    fullName: 'Distribución de intensidad',
    short: 'Distribución',
    definition:
      'Reparto del volumen semanal entre zonas (Z1 a Z5) en porcentaje.',
    interpretation:
      'Modelo polarizado: ~80 % Z1–Z2, ~20 % Z4+. Modelo piramidal: 75/15/10. Evita zona gris (mucho Z3).',
  },
  formStatus: {
    fullName: 'Estado de forma',
    short: 'Forma',
    definition:
      'Etiqueta cualitativa derivada del TSB: fresco / en forma / transición / sobrecarga.',
    interpretation:
      'TSB > +15 fresco · 0..+15 en forma · −15..0 transición · < −15 sobrecarga (riesgo).',
  },
};

/**
 * Devuelve la entrada del glosario para una clave. Asume que la clave existe
 * (TypeScript lo garantiza en compile-time vía `MetricKey`).
 */
export function getMetricEntry(key: MetricKey): MetricEntry {
  return METRICS_GLOSSARY[key];
}
